/**
 * Auth check to determine the user's mode and role.
 * Matches switchersim-v2 pattern: server returns { authenticated, user }.
 *
 * Calls GET /api/v1/auth/me:
 *   - authenticated + enterprise role → mode: 'enterprise'
 *   - authenticated + admin/manager/teacher with ?mode=enterprise → mode: 'enterprise'
 *   - authenticated + student/teacher/admin → mode: 'student'
 *   - not authenticated → mode: 'enterprise' (dev default)
 */

export interface AuthResult {
  mode: 'enterprise' | 'student';
  role: string;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    role: string;
  };
}

export async function checkAuthAndRoute(): Promise<AuthResult> {
  try {
    const res = await fetch('/api/v1/auth/me');
    if (!res.ok) {
      return { mode: 'enterprise', role: '' };
    }

    const json = await res.json();

    if (!json.authenticated || !json.user) {
      return { mode: 'enterprise', role: '' };
    }

    const user = json.user;
    const role: string = user.role ?? '';

    if (role === 'enterprise') {
      return { mode: 'enterprise', role };
    }

    // Admin/manager/teacher with ?mode=enterprise → enterprise flow (NEVCO branded).
    // Teachers reach this via the Launch Enterprise link, which the portal only
    // shows when they hold the has_enterprise_access add-on.
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'enterprise' && ['admin', 'manager', 'teacher'].includes(role)) {
      return { mode: 'enterprise', role };
    }

    // Student, teacher, admin, manager → student dashboard
    return {
      mode: 'student',
      role,
      user: {
        id: user.id ?? 0,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        role,
      },
    };
  } catch {
    // Network error or no server — default to enterprise for dev
    return { mode: 'enterprise', role: '' };
  }
}
